import { useMemo, useCallback, useReducer } from 'react';
import useRouterQueryParams from 'magik-react-hooks/useRouterQueryParams';
import { qpInt } from 'magik-react-hooks/qpUtils';
import useConstant from 'magik-react-hooks/useConstant';
import get from 'lodash.get';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function makeEncodersDecoders(dtConfig) {
  return Object.keys(dtConfig).reduce(function (encDecs, name) {
    var conf = dtConfig[name];

    if (typeof conf === 'object' && conf !== null && conf.encdec) {
      var _extends2;

      return _extends((_extends2 = {}, _extends2[name] = conf.encdec, _extends2), encDecs);
    } else {
      return encDecs;
    }
  }, {});
}
function mapFiltersToEffectValues(filters, dtConfig) {
  return Object.keys(filters).reduce(function (mapped, name) {
    var _extends3;

    var key = name;
    var value = filters[name];
    var conf = dtConfig[name];

    if (typeof conf === 'object' && conf !== null) {
      // TODO: Better name ...
      if (conf.field) {
        key = conf.field;
      }
    }

    return _extends({}, mapped, (_extends3 = {}, _extends3[key] = value, _extends3));
  }, {});
} // TODO: Implement better

function getEventOrValue(eOrValue) {
  return get(eOrValue, 'target.value', eOrValue);
}
function makeFiltersFromQueryParams(dtConfig, params) {
  return Object.keys(dtConfig).reduce(function (filters, name) {
    var _extends4;

    var conf = dtConfig[name];
    var defaultValue;

    if (typeof conf === 'object' && conf !== null) {
      defaultValue = conf.value;
    } else {
      defaultValue = conf;
    }

    var value = get(params, name, defaultValue);
    return _extends((_extends4 = {}, _extends4[name] = value, _extends4), filters);
  }, {});
}

function useQsDataTableFactory(useRunRj) {
  return function useQsDataTable(rjObject, makeDtConfig, deps) {
    if (deps === void 0) {
      deps = [];
    }

    var dtConfig = useConstant(makeDtConfig);
    var qpConfig = useConstant(function () {
      return _extends({
        page: qpInt(10)
      }, makeEncodersDecoders(dtConfig));
    });

    var _useRouterQueryParams = useRouterQueryParams(qpConfig),
        params = _useRouterQueryParams[0],
        setParams = _useRouterQueryParams[1];

    var page = params.page || 1;
    var filters = useMemo(function () {
      return makeFiltersFromQueryParams(dtConfig, params);
    }, [dtConfig, params]);
    var effectArgs = useMemo(function () {
      var effectValues = mapFiltersToEffectValues(filters, dtConfig);
      return _extends({}, effectValues, {
        page: page
      });
    }, [dtConfig, filters, page]);

    var _useRunRj = useRunRj(rjObject, [].concat(deps, [effectArgs]), false),
        state = _useRunRj[0],
        actions = _useRunRj[1];

    var withNextMeta = actions.withNextMeta,
        run = actions.run;
    var setParamsWithMeta = useCallback(function (nextParams, meta) {
      setParams(nextParams);
      withNextMeta(meta);
    }, [setParams, withNextMeta]);
    var refresh = useCallback(function () {
      run.apply(void 0, deps.concat([effectArgs]));
    }, [deps, effectArgs, run]);
    var onFiltersChange = useCallback(function (nextFilters, debounced) {
      if (debounced === void 0) {
        debounced = false;
      }

      var nextParams = _extends({}, nextFilters, {
        page: 1
      });

      setParamsWithMeta(nextParams, {
        debounced: debounced
      });
    }, [setParamsWithMeta]);
    var onFilterChange = useCallback(function (eOrValue, maybeName, debounced) {
      var _nextParams;

      if (debounced === void 0) {
        debounced = false;
      }

      var value = getEventOrValue(eOrValue); // TODO: Better error handling

      var name = maybeName || eOrValue.target.name;
      var nextParams = (_nextParams = {}, _nextParams[name] = value, _nextParams.page = 1, _nextParams);
      setParamsWithMeta(nextParams, {
        debounced: debounced
      });
    }, [setParamsWithMeta]);
    var onFilterChangeDebounced = useCallback(function (eOrValue, maybeName) {
      return onFilterChange(eOrValue, maybeName, true);
    }, [onFilterChange]);
    var makeOnFilterChange = useCallback(function (name, debounced) {
      if (debounced === void 0) {
        debounced = false;
      }

      return function (eOrValue) {
        var _nextParams2;

        var value = getEventOrValue(eOrValue);
        var nextParams = (_nextParams2 = {}, _nextParams2[name] = value, _nextParams2.page = 1, _nextParams2);
        setParamsWithMeta(nextParams, {
          debounced: debounced
        });
      };
    }, [setParamsWithMeta]);
    var goToPage = useCallback(function (page) {
      var nextParams = {
        page: page
      };
      setParamsWithMeta(nextParams, {
        debounced: false
      });
    }, [setParamsWithMeta]);
    return [state, _extends({}, filters, {
      page: page
    }), _extends({}, actions, {
      goToPage: goToPage,
      onFiltersChange: onFiltersChange,
      onFilterChange: onFilterChange,
      onFilterChangeDebounced: onFilterChangeDebounced,
      makeOnFilterChange: makeOnFilterChange,
      run: run,
      refresh: refresh
    })];
  };
}

function datatableReducer(state, _ref) {
  var _extends2;

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case 'UPDATE_FILTER':
      return {
        params: _extends({}, state.params, (_extends2 = {}, _extends2[payload.name] = payload.value, _extends2.page = 1, _extends2))
      };

    case 'UPDATE_FILTERS':
      return {
        params: _extends({}, state.params, {}, payload, {
          page: 1
        })
      };

    case 'GO_TO_PAGE':
      return {
        params: _extends({}, state.params, {
          page: payload
        })
      };

    default:
      throw new Error('Bad action 4 data table');
  }
}

function initDatatableState(dtConfig) {
  return {
    params: _extends({}, makeFiltersFromQueryParams(dtConfig, {}), {
      page: 1
    })
  };
}

function useReactDataTableFactory(useRunRj) {
  return function useReactDataTable(rjObject, makeDtConfig, deps) {
    if (deps === void 0) {
      deps = [];
    }

    var dtConfig = useConstant(makeDtConfig);

    var _useReducer = useReducer(datatableReducer, dtConfig, initDatatableState),
        filtersState = _useReducer[0],
        dispatchFilters = _useReducer[1];

    var params = filtersState.params;
    var page = params.page;
    var filters = useMemo(function () {
      return makeFiltersFromQueryParams(dtConfig, params);
    }, [dtConfig, params]);
    var effectArgs = useMemo(function () {
      var effectValues = mapFiltersToEffectValues(filters, dtConfig);
      return _extends({}, effectValues, {
        page: page
      });
    }, [dtConfig, filters, page]);

    var _useRunRj = useRunRj(rjObject, [].concat(deps, [effectArgs]), false),
        state = _useRunRj[0],
        actions = _useRunRj[1];

    var withNextMeta = actions.withNextMeta,
        run = actions.run;
    var onFiltersChange = useCallback(function (nextFilters, debounced) {
      if (debounced === void 0) {
        debounced = false;
      }

      dispatchFilters({
        type: 'UPDATE_FILTERS',
        payload: nextFilters
      });
      withNextMeta({
        debounced: debounced
      });
    }, [withNextMeta]);
    var onFilterChange = useCallback(function (eOrValue, maybeName, debounced) {
      if (debounced === void 0) {
        debounced = false;
      }

      var value = getEventOrValue(eOrValue); // TODO: Better error handling

      var name = maybeName || eOrValue.target.name;
      dispatchFilters({
        type: 'UPDATE_FILTER',
        payload: {
          name: name,
          value: value
        }
      });
      withNextMeta({
        debounced: debounced
      });
    }, [withNextMeta]);
    var refresh = useCallback(function () {
      run.apply(void 0, deps.concat([effectArgs]));
    }, [deps, effectArgs, run]);
    var onFilterChangeDebounced = useCallback(function (eOrValue, maybeName) {
      return onFilterChange(eOrValue, maybeName, true);
    }, [onFilterChange]);
    var makeOnFilterChange = useCallback(function (name, debounced) {
      if (debounced === void 0) {
        debounced = false;
      }

      return function (eOrValue) {
        var value = getEventOrValue(eOrValue);
        dispatchFilters({
          type: 'UPDATE_FILTER',
          payload: {
            name: name,
            value: value
          }
        });
        withNextMeta({
          debounced: debounced
        });
      };
    }, [withNextMeta]);
    var goToPage = useCallback(function (page) {
      dispatchFilters({
        type: 'GO_TO_PAGE',
        payload: page
      });
      withNextMeta({
        debounced: false
      });
    }, [withNextMeta]);
    return [state, _extends({}, filters, {
      page: page
    }), _extends({}, actions, {
      goToPage: goToPage,
      onFiltersChange: onFiltersChange,
      onFilterChange: onFilterChange,
      onFilterChangeDebounced: onFilterChangeDebounced,
      makeOnFilterChange: makeOnFilterChange,
      run: run,
      refresh: refresh
    })];
  };
}

export { _objectWithoutPropertiesLoose as _, useReactDataTableFactory as a, useQsDataTableFactory as u };
